import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faEye,
  faEyeSlash,
  faImage,
  faPlus,
  faSave,
  faSearch,
  faSpinner,
  faTimes,
  faTrashAlt
} from '@fortawesome/free-solid-svg-icons';
import { useMessageBox } from '../others/MessageBox';
import { getExams, getMaterials, getMockTests } from '../../services/admin.util';
import {
  createHomeCarouselItem,
  deleteHomeCarouselItem,
  getAllHomeCarouselItems,
  updateHomeCarouselItem,
  uploadHomeCarouselImage
} from '../../services/homeCarousel.util';

const itemTypeOptions = [
  { value: 'course', label: 'Course' },
  { value: 'material', label: 'Material' },
  { value: 'mockTest', label: 'Mock Test' },
  { value: 'testPaper', label: 'Test Paper' },
  { value: 'custom', label: 'Custom' },
];

const defaultThemeColor = '#F5C14B';
const hexColorPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const emptyFormData = {
  title: '',
  subtitle: '',
  category: '',
  cta: 'Explore Now',
  themeColor: '',
  itemType: 'course',
  exam: '',
  material: '',
  mockTest: '',
  route: '',
  customHtml: '',
  highlights: '',
  order: 0,
  isActive: true,
};

const escapeHtmlAttribute = (value) => (
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
);

const getUploadedImageUrl = (response) => (
  response?.data?.url ||
  response?.data?.fileUrl ||
  response?.data?.location ||
  response?.url ||
  response?.fileUrl ||
  response?.location ||
  ''
);

const getEntityTitle = (item) => {
  if (item.itemType === 'material') return item.material?.title || 'No material selected';
  if (item.itemType === 'mockTest') return item.mockTest?.title || 'No mock test selected';
  if (item.itemType === 'testPaper') {
    return item.mockTest?.title || item.exam?.title || 'No test paper source selected';
  }
  if (item.itemType === 'course') return item.exam?.title || 'No course selected';

  return item.route || 'Custom route';
};

const getTypeLabel = (itemType) => (
  itemTypeOptions.find((option) => option.value === itemType)?.label || itemType
);

const getThemeColorPreview = (color) => {
  const normalizedColor = typeof color === 'string' ? color.trim() : '';

  if (!hexColorPattern.test(normalizedColor)) return defaultThemeColor;

  return normalizedColor.length === 4
    ? `#${normalizedColor[1]}${normalizedColor[1]}${normalizedColor[2]}${normalizedColor[2]}${normalizedColor[3]}${normalizedColor[3]}`
    : normalizedColor;
};

const HomeCarouselManagement = () => {
  const { showMessage } = useMessageBox();
  const [items, setItems] = useState([]);
  const [exams, setExams] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [mockTests, setMockTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [uploadingImage, setUploadingImage] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [itemsResponse, examsResponse, materialsResponse, mockTestsResponse] = await Promise.all([
        getAllHomeCarouselItems({ active: 'all' }),
        getExams(),
        getMaterials(),
        getMockTests(),
      ]);

      if (itemsResponse.success) setItems(itemsResponse.data || []);
      if (examsResponse.success) setExams(examsResponse.data || []);
      if (materialsResponse.success) setMaterials(materialsResponse.data || []);
      if (mockTestsResponse.success) setMockTests(mockTestsResponse.data || []);
    } catch (error) {
      console.error('Error loading home carousel data:', error);
      showMessage(error.message || 'Failed to load home carousel data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setFormData(emptyFormData);
    setEditingItem(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      subtitle: item.subtitle || '',
      category: item.category || '',
      cta: item.cta || 'Explore Now',
      themeColor: item.themeColor || '',
      itemType: item.itemType || 'custom',
      exam: item.exam?._id || '',
      material: item.material?._id || '',
      mockTest: item.mockTest?._id || '',
      route: item.route || '',
      customHtml: item.customHtml || '',
      highlights: Array.isArray(item.highlights) ? item.highlights.join(', ') : '',
      order: item.order || 0,
      isActive: item.isActive !== false,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const buildPayload = () => ({
    title: formData.title.trim(),
    subtitle: formData.subtitle.trim(),
    category: formData.category.trim(),
    cta: formData.cta.trim(),
    themeColor: formData.themeColor.trim(),
    itemType: formData.itemType,
    exam: formData.exam || null,
    material: formData.material || null,
    mockTest: formData.mockTest || null,
    route: formData.route.trim(),
    customHtml: formData.itemType === 'custom' ? formData.customHtml.trim() : '',
    highlights: formData.highlights
      .split(',')
      .map((highlight) => highlight.trim())
      .filter(Boolean),
    order: parseInt(formData.order, 10) || 0,
    isActive: formData.isActive,
  });

  const handleCustomImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showMessage('Please choose an image file', 'error');
      return;
    }

    try {
      setUploadingImage(true);
      const response = await uploadHomeCarouselImage(file);
      const imageUrl = getUploadedImageUrl(response);

      if (!response.success || !imageUrl) {
        throw new Error(response.message || 'Image upload failed');
      }

      const imageSnippet = `<img src="${escapeHtmlAttribute(imageUrl)}" alt="${escapeHtmlAttribute(file.name)}" />`;
      setFormData((previous) => ({
        ...previous,
        customHtml: `${previous.customHtml || ''}${(previous.customHtml || '').trim() ? '\n' : ''}${imageSnippet}`,
      }));
      showMessage('Image uploaded and added to custom HTML', 'success');
    } catch (error) {
      console.error('Home carousel image upload error:', error);
      showMessage(error.message || 'Failed to upload image', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.title.trim()) {
      showMessage('Title is required', 'error');
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();
      const response = editingItem
        ? await updateHomeCarouselItem(editingItem._id, payload)
        : await createHomeCarouselItem(payload);

      if (response.success) {
        showMessage(`Carousel item ${editingItem ? 'updated' : 'created'} successfully!`, 'success');
        closeModal();
        await loadData();
      } else {
        throw new Error(response.message || 'Save failed');
      }
    } catch (error) {
      console.error('Home carousel save error:', error);
      showMessage(error.message || 'Failed to save carousel item', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.title}"?`)) {
      return;
    }

    try {
      const response = await deleteHomeCarouselItem(item._id);

      if (response.success) {
        showMessage('Carousel item deleted successfully!', 'success');
        await loadData();
      } else {
        throw new Error(response.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Home carousel delete error:', error);
      showMessage(error.message || 'Failed to delete carousel item', 'error');
    }
  };

  const toggleActiveStatus = async (item) => {
    try {
      const response = await updateHomeCarouselItem(item._id, {
        title: item.title,
        subtitle: item.subtitle || '',
        category: item.category,
        cta: item.cta || 'Explore Now',
        themeColor: item.themeColor || '',
        itemType: item.itemType,
        exam: item.exam?._id || null,
        material: item.material?._id || null,
        mockTest: item.mockTest?._id || null,
        route: item.route || '',
        customHtml: item.itemType === 'custom' ? item.customHtml || '' : '',
        highlights: item.highlights || [],
        order: item.order || 0,
        isActive: !item.isActive,
      });

      if (response.success) {
        showMessage(`Carousel item ${!item.isActive ? 'activated' : 'deactivated'} successfully!`, 'success');
        await loadData();
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (error) {
      console.error('Home carousel status error:', error);
      showMessage(error.message || 'Failed to update carousel item status', 'error');
    }
  };

  const filteredItems = items.filter((item) => {
    const search = searchTerm.toLowerCase();
    return (
      item.title?.toLowerCase().includes(search) ||
      item.subtitle?.toLowerCase().includes(search) ||
      item.category?.toLowerCase().includes(search) ||
      getEntityTitle(item).toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="d-flex justify-center items-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-purple-1" />
        <span className="ml-3">Loading home carousel...</span>
      </div>
    );
  }

  return (
    <div className="home-carousel-admin">
      <div className="d-flex justify-between items-center mb-4">
        <div>
          <h2 className="text-22 fw-600 mb-1">Home Carousel Management</h2>
          <p className="text-light-1 text-15">Configure homepage carousel items and display order</p>
        </div>
        <button className="button -sm -purple-1 text-white" onClick={openCreateModal}>
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Carousel Item
        </button>
      </div>

      <div className="row mb-4">
        <div className="col-md-8">
          <div className="form-group">
            <div className="single-field -search">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                className="form-control"
                placeholder="Search carousel items..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="text-14 text-light-1">
            {filteredItems.length} of {items.length} items
          </div>
        </div>
      </div>

      <div className="rounded-16 border-light overflow-hidden">
        {filteredItems.length === 0 ? (
          <div className="text-center py-5">
            <h4 className="text-18 fw-500 mb-2">No carousel items found</h4>
            <p className="text-light-1">
              {searchTerm ? 'Try adjusting your search criteria' : 'Add your first carousel item to get started'}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead className="bg-light-4">
                <tr>
                  <th className="text-14 fw-600 px-20 py-15">Order</th>
                  <th className="text-14 fw-600 px-20 py-15">Carousel Item</th>
                  <th className="text-14 fw-600 px-20 py-15">Type</th>
                  <th className="text-14 fw-600 px-20 py-15">Target</th>
                  <th className="text-14 fw-600 px-20 py-15">Status</th>
                  <th className="text-14 fw-600 px-20 py-15">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item._id} className="border-bottom-light">
                    <td className="px-20 py-15">
                      <span className="badge badge-outline text-12">{item.order || 0}</span>
                    </td>
                    <td className="px-20 py-15">
                      <div>
                        <div className="text-15 fw-500 mb-1">{item.title}</div>
                        <div className="text-13 text-light-1">
                          {item.subtitle?.substring(0, 70)}
                          {item.subtitle?.length > 70 && '...'}
                        </div>
                        <div className="mt-2">
                          <span className="badge badge-light mr-1 text-10">{item.category}</span>
                          {item.cta && <span className="badge badge-light text-10">{item.cta}</span>}
                          {item.themeColor && (
                            <span className="badge badge-light text-10 ml-1">
                              <span
                                className="home-carousel-color-dot"
                                style={{ backgroundColor: getThemeColorPreview(item.themeColor) }}
                              />
                              {item.themeColor}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-20 py-15">
                      <span className="badge badge-outline text-12">{getTypeLabel(item.itemType)}</span>
                    </td>
                    <td className="px-20 py-15">
                      <div className="text-14 fw-500">{getEntityTitle(item)}</div>
                      {item.route && <div className="text-12 text-light-1">{item.route}</div>}
                    </td>
                    <td className="px-20 py-15">
                      <button
                        className={`badge ${item.isActive ? 'badge-success' : 'badge-secondary'} cursor-pointer`}
                        onClick={() => toggleActiveStatus(item)}
                        title={`Click to ${item.isActive ? 'deactivate' : 'activate'}`}
                      >
                        <FontAwesomeIcon icon={item.isActive ? faEye : faEyeSlash} className="mr-1" />
                        {item.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-20 py-15">
                      <div className="d-flex items-center">
                        <button
                          className="button -sm -outline-purple-1 text-purple-1 mr-2"
                          onClick={() => openEditModal(item)}
                          title="Edit"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          className="button -sm -outline-red-1 text-red-1"
                          onClick={() => handleDelete(item)}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="home-carousel-dialog">
            <div className="home-carousel-modal">
              <div className="home-carousel-modal-header">
                <h5 className="home-carousel-modal-title">{editingItem ? 'Edit Carousel Item' : 'Add Carousel Item'}</h5>
                <button className="home-carousel-modal-close" onClick={closeModal} disabled={saving}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="home-carousel-modal-body">
                  <div className="row">
                    <div className="col-md-8">
                      <div className="form-group">
                        <label className="form-label">Title *</label>
                        <input
                          type="text"
                          name="title"
                          className="form-control"
                          value={formData.title}
                          onChange={handleInputChange}
                          placeholder="Carousel title"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="form-label">Order</label>
                        <input
                          type="number"
                          name="order"
                          className="form-control"
                          value={formData.order}
                          onChange={handleInputChange}
                          min="0"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="form-label">Type</label>
                        <select
                          name="itemType"
                          className="form-select"
                          value={formData.itemType}
                          onChange={handleInputChange}
                        >
                          {itemTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="form-label">Category</label>
                        <input
                          type="text"
                          name="category"
                          className="form-control"
                          value={formData.category}
                          onChange={handleInputChange}
                          placeholder="e.g., Course, Materials"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="form-label">CTA</label>
                        <input
                          type="text"
                          name="cta"
                          className="form-control"
                          value={formData.cta}
                          onChange={handleInputChange}
                          placeholder="Explore Now"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="form-label">Theme Color</label>
                        <div className="home-carousel-color-field">
                          <input
                            type="color"
                            className="home-carousel-color-picker"
                            value={getThemeColorPreview(formData.themeColor)}
                            onChange={(event) => {
                              setFormData((previous) => ({
                                ...previous,
                                themeColor: event.target.value,
                              }));
                            }}
                            aria-label="Pick carousel theme color"
                          />
                          <input
                            type="text"
                            name="themeColor"
                            className="form-control"
                            value={formData.themeColor}
                            onChange={handleInputChange}
                            placeholder={defaultThemeColor}
                          />
                        </div>
                        <small className="form-text text-muted">
                          Optional. Leave blank to use the default gold.
                        </small>
                      </div>
                    </div>

                    {(formData.itemType === 'course' || formData.itemType === 'testPaper') && (
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Course</label>
                          <select
                            name="exam"
                            className="form-select"
                            value={formData.exam}
                            onChange={handleInputChange}
                          >
                            <option value="">Select course</option>
                            {exams.map((exam) => (
                              <option key={exam._id} value={exam._id}>{exam.title}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {formData.itemType === 'material' && (
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Material Package</label>
                          <select
                            name="material"
                            className="form-select"
                            value={formData.material}
                            onChange={handleInputChange}
                          >
                            <option value="">Select material</option>
                            {materials.map((material) => (
                              <option key={material._id} value={material._id}>{material.title}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {(formData.itemType === 'mockTest' || formData.itemType === 'testPaper') && (
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="form-label">Mock Test Package</label>
                          <select
                            name="mockTest"
                            className="form-select"
                            value={formData.mockTest}
                            onChange={handleInputChange}
                          >
                            <option value="">Select mock test package</option>
                            {mockTests.map((mockTest) => (
                              <option key={mockTest._id} value={mockTest._id}>{mockTest.title}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="form-label">Route Override</label>
                        <input
                          type="text"
                          name="route"
                          className="form-control"
                          value={formData.route}
                          onChange={handleInputChange}
                          placeholder="/exam/course-slug"
                        />
                        <small className="form-text text-muted">
                          Optional. Leave blank to use the selected entity route.
                        </small>
                      </div>
                    </div>
                    {formData.itemType === 'custom' && (
                      <div className="col-12">
                        <div className="form-group">
                          <label className="form-label">Custom HTML</label>
                          <textarea
                            name="customHtml"
                            className="form-control home-carousel-custom-html"
                            rows="8"
                            value={formData.customHtml}
                            onChange={handleInputChange}
                            placeholder='<div class="custom-carousel-slide"><h2>Custom offer</h2><p>Write the HTML to show in the carousel.</p></div>'
                          />
                          <div className="home-carousel-upload-row">
                            <label className={`button -sm -outline-purple-1 text-purple-1 ${uploadingImage ? 'disabled' : ''}`}>
                              {uploadingImage ? (
                                <>
                                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faImage} className="mr-2" />
                                  Upload Image to S3
                                </>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleCustomImageUpload}
                                disabled={uploadingImage}
                                hidden
                              />
                            </label>
                            <small className="form-text text-muted">
                              Uploaded images are inserted into the HTML as S3-backed image tags.
                            </small>
                          </div>
                          <small className="form-text text-muted">
                            Scripts and inline event handlers are removed before this HTML is displayed on the homepage.
                          </small>
                        </div>
                      </div>
                    )}
                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">Subtitle</label>
                        <textarea
                          name="subtitle"
                          className="form-control"
                          rows="3"
                          value={formData.subtitle}
                          onChange={handleInputChange}
                          placeholder="Short carousel description"
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">Highlights</label>
                        <input
                          type="text"
                          name="highlights"
                          className="form-control"
                          value={formData.highlights}
                          onChange={handleInputChange}
                          placeholder="Separate highlights with commas"
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          name="isActive"
                          className="form-check-input"
                          id="homeCarouselActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="homeCarouselActive">
                          Show this item in the home carousel
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="home-carousel-modal-footer">
                  <button
                    type="button"
                    className="button -outline-dark-1 text-dark-1 mr-3"
                    onClick={closeModal}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="button -purple-1 text-white" disabled={saving}>
                    {saving ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        Save Item
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .home-carousel-modal {
              background: #ffffff;
              border: 0;
              border-radius: 18px;
              box-shadow: 0 28px 80px rgba(15, 23, 42, 0.28);
              display: flex;
              flex-direction: column;
              max-height: calc(100vh - 40px) !important;
              max-width: 100% !important;
              width: 100% !important;
              overflow: hidden !important;
            }

            .home-carousel-dialog {
              width: min(760px, calc(100vw - 40px)) !important;
              max-width: 760px !important;
              margin: 0 !important;
              background: transparent !important;
            }

            .home-carousel-modal form {
              display: flex;
              flex: 1;
              flex-direction: column;
              min-height: 0;
            }

            .home-carousel-modal-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 24px 32px;
              border-bottom: 1px solid #e5e7eb;
            }

            .home-carousel-modal-title {
              color: #111827;
              font-size: 20px;
              font-weight: 700;
              margin: 0;
            }

            .home-carousel-modal-close {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 34px;
              height: 34px;
              color: #6b7280;
              background: transparent;
              border: 0;
              border-radius: 999px;
              cursor: pointer;
              transition: color 0.18s ease, background-color 0.18s ease;
            }

            .home-carousel-modal-close:hover:not(:disabled) {
              color: #111827;
              background: #f3f4f6;
            }

            .home-carousel-modal-body {
              flex: 1;
              min-height: 0;
              overflow-y: auto !important;
              padding: 26px 32px 18px;
            }

            .home-carousel-modal .form-group {
              margin-bottom: 18px;
            }

            .home-carousel-modal .form-label {
              display: block;
              margin-bottom: 8px;
              color: #374151;
              font-size: 13px;
              font-weight: 600;
            }

            .home-carousel-modal .form-control,
            .home-carousel-modal .form-select {
              width: 100%;
              min-height: 44px;
              padding: 10px 12px;
              color: #111827;
              background-color: #ffffff;
              border: 1px solid #d1d5db !important;
              border-radius: 8px;
              box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
              transition: border-color 0.2s ease, box-shadow 0.2s ease;
            }

            .home-carousel-modal textarea.form-control {
              min-height: 92px;
              resize: vertical;
            }

            .home-carousel-modal textarea.home-carousel-custom-html {
              min-height: 190px;
              font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
              font-size: 12px;
              line-height: 1.5;
            }

            .home-carousel-modal .form-control::placeholder {
              color: #9ca3af;
            }

            .home-carousel-modal .form-control:focus,
            .home-carousel-modal .form-select:focus {
              border-color: #6440fb !important;
              box-shadow: 0 0 0 3px rgba(100, 64, 251, 0.14);
              outline: none;
            }

            .home-carousel-color-field {
              display: flex;
              align-items: center;
              gap: 10px;
            }

            .home-carousel-color-picker {
              flex: 0 0 48px;
              width: 48px;
              height: 44px;
              padding: 4px;
              background: #ffffff;
              border: 1px solid #d1d5db;
              border-radius: 8px;
              cursor: pointer;
            }

            .home-carousel-color-dot {
              display: inline-block;
              width: 9px;
              height: 9px;
              margin-right: 4px;
              border-radius: 50%;
              box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
              vertical-align: -1px;
            }

            .home-carousel-upload-row {
              display: flex;
              align-items: center;
              flex-wrap: wrap;
              gap: 10px;
              margin-top: 10px;
            }

            .home-carousel-upload-row .button {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              min-height: 36px;
              margin: 0 !important;
              cursor: pointer;
            }

            .home-carousel-upload-row .button.disabled {
              cursor: not-allowed;
              opacity: 0.65;
            }

            .home-carousel-upload-row .form-text {
              margin-top: 0;
            }

            .home-carousel-modal .form-text {
              display: block;
              margin-top: 6px;
              color: #6b7280 !important;
              font-size: 12px;
              line-height: 1.45;
            }

            .home-carousel-modal .form-check {
              display: flex;
              align-items: center;
              gap: 10px;
              margin-top: 4px;
              padding: 12px 14px;
              border: 1px solid #e5e7eb;
              border-radius: 10px;
              background: #f9fafb;
            }

            .home-carousel-modal .form-check-input {
              width: 16px;
              height: 16px;
              margin: 0;
            }

            .home-carousel-modal .form-check-label {
              color: #374151;
              font-size: 14px;
              font-weight: 500;
            }

            .home-carousel-modal-footer {
              display: flex;
              justify-content: flex-end;
              gap: 12px;
              padding: 20px 32px 26px;
              border-top: 1px solid #e5e7eb;
            }

            .home-carousel-modal-footer .button {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              min-width: 112px;
              min-height: 40px;
              padding: 0 18px;
              margin-right: 0 !important;
              border-radius: 8px;
              font-size: 14px;
              font-weight: 700;
              line-height: 1;
              text-decoration: none;
              transition: transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease, border-color 0.18s ease;
            }

            .home-carousel-modal-footer .button.-outline-dark-1 {
              color: #374151 !important;
              background: #ffffff;
              border: 1px solid #d1d5db;
              box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
            }

            .home-carousel-modal-footer .button.-outline-dark-1:hover:not(:disabled) {
              color: #111827 !important;
              background: #f9fafb;
              border-color: #9ca3af;
            }

            .home-carousel-modal-footer .button.-purple-1 {
              color: #ffffff !important;
              background: #6440fb;
              border: 1px solid #6440fb;
              box-shadow: 0 8px 18px rgba(100, 64, 251, 0.24);
            }

            .home-carousel-modal-footer .button.-purple-1:hover:not(:disabled) {
              background: #5533e8;
              border-color: #5533e8;
              box-shadow: 0 10px 22px rgba(100, 64, 251, 0.3);
              transform: translateY(-1px);
            }

            .home-carousel-modal-footer .button:disabled {
              cursor: not-allowed;
              opacity: 0.65;
              transform: none;
              box-shadow: none;
            }

            @media (max-width: 767px) {
              .home-carousel-modal-header,
              .home-carousel-modal-body,
              .home-carousel-modal-footer {
                padding-left: 20px;
                padding-right: 20px;
              }
            }
          `,
        }}
      />
    </div>
  );
};

export default HomeCarouselManagement;
